import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { AuthService, isDemoSession } from './auth.service';
import { BrandingService } from './branding.service';
import { environment } from '../../environments/environment';

export const NO_INTERCEPT = new HttpContextToken<boolean>(() => false);

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
  null,
);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const $auth = inject(AuthService);
  const $branding = inject(BrandingService);

  if (req.context.get(NO_INTERCEPT)) return next(req);

  const token = $auth.getAuthToken();
  const brandUuid = $branding.brandUuid()?.trim() ?? '';
  let authReq = req.clone({
    setHeaders: {
      'X-App-Origin': environment.appID,
      'X-GGM-Origin-Channel': 'WEB',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(brandUuid ? { 'x-brand-uuid': brandUuid } : {}),
      ...(isDemoSession() ? { 'X-GGM-Test-Session': 'true' } : {}),
    },
  });

  return next(authReq).pipe(
    catchError((error) => {
      // CLAVE 2: Si el error es 401 pero la URL es la de 'refresh', NO REINTENTAR
      // Si el refresh mismo da 401, hay que desloguear sí o sí.
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (req.url.includes('sso-exchange') || req.url.includes('demo-access')) {
          return throwError(() => error);
        }
        if (req.url.includes('refresh') || req.url.includes('login')) {
          $auth.removeAuthToken();
          return throwError(() => error);
        }
        return handle401Error(authReq, next, $auth);
      }
      return throwError(() => error);
    }),
  );
};

export function resetAuthInterceptorState(): void {
  isRefreshing = false;
  refreshTokenSubject.next(null);
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  $auth: AuthService,
): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return $auth.refreshToken().pipe(
      switchMap((newToken) => {
        isRefreshing = false;
        refreshTokenSubject.next(newToken);

        // Reintentamos la petición original con el token NUEVO
        return next(addAuthHeader(request, newToken));
      }),
      catchError((err) => {
        isRefreshing = false;
        refreshTokenSubject.next(null); // Limpiar para futuros intentos tras login
        $auth.removeAuthToken();
        return throwError(() => err);
      }),
    );
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        return next(addAuthHeader(request, token!));
      }),
    );
  }
}

function addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}
